import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Clock3, FileSearch, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';

const formatTimestamp = (value) => {
  if (!value) return 'Time unavailable';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const safeAnswerRows = (submission) => {
  const source = submission?.overview || submission?.answers || submission?.summary || {};
  if (Array.isArray(source)) {
    return source.slice(0, 100).map((row, index) => ({
      key: String(row?.uid ?? row?.field_key ?? index),
      label: String(row?.label ?? row?.name ?? row?.uid ?? `Answer ${index + 1}`),
      value: row?.value,
    }));
  }
  if (!source || typeof source !== 'object') return [];
  return Object.entries(source).slice(0, 100).map(([key, value]) => ({ key, label: key, value }));
};

const displayAnswer = (value) => {
  if (value === null || value === undefined || value === '') return 'Not answered';
  if (Array.isArray(value)) return value.slice(0, 50).map(displayAnswer).join(', ').slice(0, 2000);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return 'Structured answer';
  return String(value).slice(0, 2000);
};

const statusDetails = (status) => {
  const normalized = String(status || 'pending_review').toLowerCase();
  if (normalized === 'applied' || normalized === 'complete') return { label: 'Applied', icon: CheckCircle2 };
  if (normalized === 'rejected') return { label: 'Rejected', icon: ShieldAlert };
  return { label: 'Pending review', icon: Clock3 };
};

const applicationMessage = (submission) => {
  if (String(submission?.status || '').toLowerCase() !== 'applied') {
    return 'This legacy entry is retained for authorized review and has not been applied automatically.';
  }
  const target = submission?.application_target;
  if (!target || !target.target_type || !target.target_id) {
    return 'The supported answers were applied when this form was submitted.';
  }
  const matter = `${target.target_type === 'case' ? 'case' : 'lead'} #${target.target_id}`;
  return target.created_new
    ? `CaseDB created ${matter} and applied the supported answers in the submission transaction.`
    : `CaseDB applied the supported answers to ${matter} in the submission transaction.`;
};

const applicationSkipMessages = {
  target_nonempty: 'Not saved to CaseDB: the existing value was kept.',
  repeat_group_unmapped: 'Not saved to CaseDB: repeated answers do not have a field mapping.',
  submission_only_field: 'Kept in this submission only: this question is not mapped to a CaseDB field.',
  target_scope_conflict: 'Not saved to CaseDB: the field could not be matched safely to this matter.',
  unsupported_value_shape: 'Not saved to CaseDB: this answer format is not supported for the field.',
  unsupported_value: 'Not saved to CaseDB: this answer is not supported for the field.',
};

const safeApplicationRows = (submission, answerRows) => {
  const summary = submission?.application_summary;
  if (String(submission?.status || '').toLowerCase() !== 'applied'
    || !summary || typeof summary !== 'object' || Array.isArray(summary)
    || !Array.isArray(summary.applied)
    || !summary.skipped || typeof summary.skipped !== 'object'
    || (Array.isArray(summary.skipped) && summary.skipped.length > 0)) {
    return null;
  }
  const labels = new Map(answerRows.map((answer) => [answer.key, answer.label]));
  const rows = [];
  const seen = new Set();
  const append = (uids, outcome, message) => {
    if (!Array.isArray(uids)) return;
    uids.slice(0, 100).forEach((uid) => {
      if (typeof uid !== 'string' || !uid.trim() || rows.length >= 100) return;
      const key = `${outcome}:${uid}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ key, label: labels.get(uid) || `Field ${uid.slice(0, 200)}`, message });
    });
  };
  append(summary.applied, 'applied', 'Saved to CaseDB.');
  Object.entries(summary.skipped).slice(0, 100).forEach(([reason, uids]) => {
    const message = Object.prototype.hasOwnProperty.call(applicationSkipMessages, reason)
      ? applicationSkipMessages[reason]
      : 'Not saved to CaseDB automatically. Review this answer.';
    append(uids, reason, message);
  });
  return rows;
};

const PublicFormSubmissionsPanel = ({ form, endpoint, onBack, linkId = null, embedded = false }) => {
  const [submissions, setSubmissions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [capabilities, setCapabilities] = useState({});
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const listController = useRef(null);
  const reviewController = useRef(null);

  const load = useCallback(async () => {
    listController.current?.abort();
    reviewController.current?.abort();
    const controller = new AbortController();
    listController.current = controller;
    setSelectedId(null);
    setReview(null);
    setReviewError('');
    setReviewLoading(false);
    setSubmissions([]);
    setCapabilities({});
    setLoading(true);
    setError('');
    if (!form?.id) {
      setLoading(false);
      return;
    }
    try {
      const options = { endpoint, signal: controller.signal };
      if (linkId !== null) options.linkId = linkId;
      if (linkId !== null || page > 1) Object.assign(options, { page, perPage: 25 });
      const payload = await publicFormsApi.submissions(form.id, options);
      if (controller.signal.aborted) return;
      const reportedPages = Number(payload.pagination?.total_pages);
      const nextTotalPages = Number.isSafeInteger(reportedPages) && reportedPages > 0 ? reportedPages : 1;
      setTotalPages(nextTotalPages);
      if (page > nextTotalPages) {
        setPage(nextTotalPages);
        return;
      }
      setSubmissions(Array.isArray(payload.submissions) ? payload.submissions : []);
      setCapabilities(payload.capabilities || {});
    } catch (requestError) {
      if (!controller.signal.aborted) setError(publicFormFeatureError(requestError));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [endpoint, form?.id, linkId, page]);

  useEffect(() => {
    load();
    return () => {
      listController.current?.abort();
      reviewController.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!form?.id || !selectedId) {
      setReview(null);
      setReviewError('');
      setReviewLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    reviewController.current = controller;
    setReview(null);
    setReviewError('');
    setReviewLoading(true);
    publicFormsApi.submission(form.id, selectedId, { endpoint, signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted) return;
        setReview(payload.submission || null);
        setCapabilities((current) => ({ ...current, ...(payload.capabilities || {}) }));
      })
      .catch((requestError) => {
        if (!controller.signal.aborted && requestError?.name !== 'AbortError') setReviewError(publicFormFeatureError(requestError));
      })
      .finally(() => {
        if (!controller.signal.aborted) setReviewLoading(false);
      });
    return () => controller.abort();
  }, [endpoint, form?.id, linkId, page, selectedId]);

  const selectedSummary = submissions.find((submission) => String(submission.id) === String(selectedId)) || null;
  const selected = review || selectedSummary;
  const answerRows = review ? safeAnswerRows(review) : [];
  const applicationRows = review ? safeApplicationRows(review, answerRows) : null;
  const changePage = (nextPage) => {
    reviewController.current?.abort();
    setSelectedId(null);
    setReview(null);
    setReviewError('');
    setPage(nextPage);
  };
  const Wrapper = embedded ? 'section' : 'div';
  const Heading = embedded ? 'h3' : 'h2';
  const heading = embedded && linkId !== null
    ? `Submissions for link #${linkId}`
    : form?.name || form?.title || 'Submissions';

  return (
    <Wrapper className="public-form-submissions" aria-label={embedded ? heading : undefined}>
      <header id={embedded ? undefined : 'page-header'} className={embedded ? 'public-form-panel-heading' : 'public-forms-header public-form-builder-header'}>
        <button type="button" className="action-micro quaternary" onClick={onBack} aria-label={embedded ? 'Back to recipient links' : 'Back to public forms'}><ArrowLeft size={18} /></button>
        <div className="public-forms-header-copy">
          <Heading>{heading}</Heading>
          <p className="subtext">Review submitted entries and their captured answers.</p>
        </div>
        <button type="button" className="action quaternary" onClick={load} disabled={loading}>
          {loading ? <Loader2 size={15} className="spinner" /> : <RefreshCw size={15} />} Refresh
        </button>
      </header>

      {error && <div className="public-form-error" role="alert">{error}</div>}

      <div className="public-form-submission-layout">
        <section className="public-form-submission-list" aria-label="Form submissions">
          {loading && submissions.length === 0 && <div className="public-form-loading"><Loader2 className="spinner" /> Loading submissions…</div>}
          {!loading && !error && submissions.length === 0 && (
            <div className="public-form-empty-state compact">
              <FileSearch size={24} />
              <strong>No entries yet</strong>
              <p>{capabilities.field_application === true
                ? 'Completed forms will appear here with their CaseDB application and notification status.'
                : 'Completed forms will appear here for authorized review.'}</p>
            </div>
          )}
          {submissions.map((submission) => {
            const status = statusDetails(submission.status);
            const StatusIcon = status.icon;
            return (
              <button
                type="button"
                key={submission.id}
                className={String(selectedId) === String(submission.id) ? 'active' : ''}
                onClick={() => {
                  if (String(selectedId) === String(submission.id)) return;
                  reviewController.current?.abort();
                  setReview(null);
                  setReviewError('');
                  setSelectedId(submission.id);
                }}
              >
                <StatusIcon size={17} />
                <span>
                  <strong>{submission.submitter_name || submission.overview_title || `Entry ${submission.id}`}</strong>
                  <small>{formatTimestamp(submission.submitted_at || submission.created_at)}</small>
                </span>
                <em>{status.label}</em>
              </button>
            );
          })}
          {totalPages > 1 && <nav className="pagination-controls" aria-label="Submission pages">
            <button type="button" className="action quaternary" disabled={loading || page <= 1} onClick={() => changePage(page - 1)}>Previous</button>
            <span className="subtext">Page {page} of {totalPages}</span>
            <button type="button" className="action quaternary" disabled={loading || page >= totalPages} onClick={() => changePage(page + 1)}>Next</button>
          </nav>}
        </section>

        <section className="public-form-submission-detail" aria-live="polite">
          {!selectedSummary ? (
            <div className="public-form-empty-state compact">
              <FileSearch size={24} />
              <strong>Select an entry</strong>
              <p>Review its form overview and captured answers.</p>
            </div>
          ) : (
            <>
              <div className="public-form-panel-heading">
                <div>
                  <span className="public-forms-eyebrow">Entry {selectedSummary.id}</span>
                  <h3>{selected?.submitter_name || selected?.overview_title || 'Submitted form'}</h3>
                </div>
                <p>{formatTimestamp(selected?.submitted_at || selectedSummary.submitted_at || selectedSummary.created_at)}</p>
              </div>
              {reviewLoading ? (
                <div className="public-form-loading"><Loader2 className="spinner" /> Decrypting entry for review…</div>
              ) : reviewError ? (
                <div className="public-form-error" role="alert">{reviewError}</div>
              ) : (
                <div className="public-form-review-fields public-form-answer-review">
                  {answerRows.length > 0 ? answerRows.map((answer) => (
                    <div key={answer.key}>
                      <strong>{answer.label}</strong>
                      <p>{displayAnswer(answer.value)}</p>
                    </div>
                  )) : (
                    <div>
                      <strong>Answers</strong>
                      <p>{capabilities.answers_decryption === true
                        ? 'No answers were captured for this entry.'
                        : 'Answer viewing is unavailable for this entry.'}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="public-form-security-callout">
                {String(selected?.status || '').toLowerCase() === 'applied'
                  ? <CheckCircle2 size={18} />
                  : <ShieldAlert size={18} />}
                <p>{applicationMessage(selected)}</p>
              </div>
              {applicationRows !== null && (
                <section aria-label="Answer application">
                  <h4>Answer application</h4>
                  <div className="public-form-review-fields public-form-answer-review">
                    {applicationRows.length > 0 ? applicationRows.map((row) => (
                      <div key={row.key}>
                        <strong>{row.label}</strong>
                        <p>{row.message}</p>
                      </div>
                    )) : (
                      <div><p>No answers were saved to CaseDB for this submission.</p></div>
                    )}
                  </div>
                </section>
              )}
              {selected?.case_setup && (
                <div className="public-form-security-callout">
                  {selected.case_setup.status === 'complete'
                    ? <CheckCircle2 size={18} />
                    : <Clock3 size={18} />}
                  <p>{selected.case_setup.status === 'complete'
                    ? `Case folders are ready. ${selected.case_setup.processed_folder_count} folders and ${selected.case_setup.processed_file_count} template files were processed.`
                    : `Case folder setup is ${selected.case_setup.status}. The durable worker has made ${selected.case_setup.attempt_count} attempt${selected.case_setup.attempt_count === 1 ? '' : 's'}.`}</p>
                </div>
              )}
              {Array.isArray(selected?.notification_deliveries) && selected.notification_deliveries.length > 0 && (
                <div className="public-form-security-callout">
                  <Clock3 size={18} />
                  <p>{selected.notification_deliveries.filter((delivery) => delivery.status === 'sent').length} of {selected.notification_deliveries.length} configured notification deliveries completed.</p>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </Wrapper>
  );
};

// Remount scoped state before rendering a different form/link, so neither an
// old list nor previously decrypted answers can flash under the new heading.
const PublicFormSubmissions = (props) => <PublicFormSubmissionsPanel
  key={JSON.stringify([props.form?.id, props.endpoint, props.linkId ?? null])}
  {...props}
/>;

export default PublicFormSubmissions;
